import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));

    // Stockomax : prechargement de la fiche a l'intention (survol 120 ms du bouton ou focus clavier)
    this.addEventListener('pointerenter', this.#schedulePrefetch, { signal: this.#cartUpdateAbortController.signal });
    this.addEventListener('pointerleave', this.#cancelPrefetch, { signal: this.#cartUpdateAbortController.signal });
    this.addEventListener('focusin', this.#prefetch, { signal: this.#cartUpdateAbortController.signal });
  }

  /** @type {number | undefined} */
  #prefetchTimer;

  /** @param {PointerEvent} event */
  #schedulePrefetch = (event) => {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(this.#prefetchTimer);
    this.#prefetchTimer = window.setTimeout(this.#prefetch, 120);
  };

  #cancelPrefetch = () => clearTimeout(this.#prefetchTimer);

  #prefetch = async () => {
    const url = this.productPageUrl;
    if (!url || this.#cachedContent.has(url) || this.#prefetching.has(url)) return;
    this.#prefetching.add(url);
    try {
      const response = await fetch(url);
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const gridElement = html.querySelector('[data-product-grid-content]');
      if (gridElement) this.#cachedContent.set(url, /** @type {Element} */ (gridElement.cloneNode(true)));
    } catch (error) {
      // prechargement opportuniste : silencieux
    } finally {
      this.#prefetching.delete(url);
    }
  };

  /** @type {Set<string>} */
  #prefetching = new Set();

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker} */ (modalContent.querySelector('variant-picker'));
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;
    this.#returnFocusTo =
      /** @type {HTMLElement | null} */ (event.target instanceof Element ? event.target.closest('button, a') : null) ||
      /** @type {HTMLElement | null} */ (document.activeElement);

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Stockomax : ouverture immediate avec squelette pendant le chargement
      const modalContent = document.getElementById('quick-add-modal-content');
      if (modalContent) {
        modalContent.innerHTML =
          '<div class="sx-qa-skeleton" aria-busy="true" aria-label="Chargement du produit"><div class="sx-qa-skeleton__media"></div><div class="sx-qa-skeleton__body"><span></span><span></span><span></span><span></span><span></span></div></div>';
      }
      this.#openQuickAddModal();

      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
    }

    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog) || !dialogComponent.refs.dialog?.open) this.#openQuickAddModal();
  };

  /** @type {HTMLElement | null} */
  #returnFocusTo = null;

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => {
        this.toggleAttribute('stay-visible', false);
        // Retour du focus sur le declencheur, sauf si une autre fenetre (panier) vient de s'ouvrir
        const target = this.#returnFocusTo;
        if (target && target.isConnected && !document.querySelector('dialog[open]')) {
          target.focus({ preventScroll: true });
        }
      },
      { once: true }
    );
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    // Stockomax : meme contenu complet sur mobile (feuille une colonne geree en CSS)
    if (false && isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    // Lien vers la fiche complete en bas du panneau
    const details = modalContent.querySelector('.product-details .group-block-content') || modalContent.querySelector('.product-details');
    if (details && !details.querySelector('.sx-qa-full') && this.productPageUrl) {
      const link = document.createElement('a');
      link.className = 'sx-qa-full';
      link.href = this.productPageUrl;
      link.textContent = 'Voir la fiche complète';
      details.appendChild(link);
    }
    modalContent.querySelectorAll('details.sx-desc[open]').forEach((d) => d.removeAttribute('open'));

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);

    // Stockomax : glisser vers le bas pour fermer (feuille mobile)
    const signal = this.#abortController.signal;
    this.addEventListener('touchstart', this.#onTouchStart, { passive: true, signal });
    this.addEventListener('touchmove', this.#onTouchMove, { passive: true, signal });
    this.addEventListener('touchend', this.#onTouchEnd, { signal });
    this.addEventListener('touchcancel', this.#onTouchEnd, { signal });
  }

  #dragStartY = 0;
  #dragDelta = 0;
  #dragging = false;

  /** @param {TouchEvent} event */
  #onTouchStart = (event) => {
    const dialog = this.refs.dialog;
    if (!dialog?.open || window.innerWidth >= 750 || event.touches.length !== 1) return;
    const scroller = dialog.querySelector('.quick-add-modal__content');
    const onHandle = /** @type {Element} */ (event.target).closest('.sx-qa-handle');
    if (!onHandle && scroller && scroller.scrollTop > 0) return;
    if (/** @type {Element} */ (event.target).closest('slideshow-slides, input, select, textarea')) return;
    this.#dragging = true;
    this.#dragStartY = event.touches[0]?.clientY ?? 0;
    this.#dragDelta = 0;
  };

  /** @param {TouchEvent} event */
  #onTouchMove = (event) => {
    if (!this.#dragging) return;
    this.#dragDelta = Math.max(0, (event.touches[0]?.clientY ?? 0) - this.#dragStartY);
    const dialog = this.refs.dialog;
    if (dialog) {
      dialog.style.transition = 'none';
      dialog.style.transform = `translateY(${this.#dragDelta}px)`;
    }
  };

  #onTouchEnd = () => {
    if (!this.#dragging) return;
    this.#dragging = false;
    const dialog = this.refs.dialog;
    if (!dialog) return;
    dialog.style.transition = 'transform 200ms cubic-bezier(0.2, 0.8, 0.2, 1)';
    if (this.#dragDelta > 110) {
      dialog.style.transform = '';
      dialog.style.transition = '';
      this.closeDialog();
    } else {
      dialog.style.transform = '';
      window.setTimeout(() => (dialog.style.transition = ''), 220);
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
